// native
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';

// service
import { ApiService } from './api.service';
import { GlobalsService } from './globals.service';
import { CollectionsService } from './collections.service';
import { UtilityService } from './utility.service';

// models
import { IDisplayItem, IItemDisplayArticle, IExtIds, IItem } from '../../models';

// constants
import { BULK_REQUEST_SIZE } from '../../constants/constants';

interface IBulkCreateRequest {
  data: IBulkCreateRequestItem[];
}

interface IBulkCreateRequestItem {
  type: string;
  action: string;
  id: string;
  data: {
    article: Partial<IItemDisplayArticle>,
    ext_ids: IExtIds;
    skip_resolve: boolean;
  };
}

interface IBulkCreateResponse {
  result: IBulkCreateResponseItem[];
  status: string;
}

interface IBulkCreateResponseItem {
  merged: boolean;
  saved: boolean;
  object: IDisplayItem;
}

@Injectable({
  providedIn: 'root'
})
export class BulkCreateItemService {

  constructor(
    private apiService: ApiService,
    private globalsService: GlobalsService,
    private collectionsService: CollectionsService,
    private utilityService: UtilityService
  ) { }

  bulkCreateItems(items: IDisplayItem[], fromDocument: boolean = false, targetLibraryId: string = null): Promise<IItem[]> {
    const libraryId = targetLibraryId || this.collectionsService.personalLibrary.id;
    const endpointUrl = `${this.globalsService.appConfig.environment.baseUrls.sync}/collections/${libraryId}/bulk`;

    const jobs: (() => Promise<IItem[]>)[] = this.utilityService.chunkify(items, BULK_REQUEST_SIZE).map((batch: IDisplayItem[]) => () => {
      const body: IBulkCreateRequest = {
        data: fromDocument ? this.mapDocumentItemsToBulkRequest(batch) : this.mapSearchItemsToBulkRequest(batch)
      };

      return this.apiService.post(endpointUrl, body).pipe(
        map((response: IBulkCreateResponse) => this.mapBulkResponseToItems(response))
      ).toPromise();
    });

    return this.utilityService.runInSequenceAsList((jobs));
  }

  private mapSearchItemsToBulkRequest = (searchItems: IDisplayItem[]): IBulkCreateRequestItem[] => {
    return searchItems.map((item: IDisplayItem) => {
      return {
        type: 'item',
        action: 'create',
        id: item.id,
        data: {
          article: item.article,
          ext_ids: item.ext_ids,
          resolve: true,
          skip_resolve: false,
          merge: true,
          skip_merge: false
        }
      };
    });
  };

  private mapDocumentItemsToBulkRequest = (searchItems: IDisplayItem[]): IBulkCreateRequestItem[] => {
    return searchItems.map((item: IDisplayItem) => {
      const canResolve = !!item.article?.title || !!item.article?.authors?.length;

      return {
        type: 'item',
        action: 'create',
        id: item.id,
        data: {
          article: item.article,
          custom_metadata: item.custom_metadata,
          user_data: item.user_data,
          import_data: item.import_data,
          item_type: item.item_type,

          ext_ids: item.ext_ids,
          resolve: canResolve,
          skip_resolve: !canResolve,
          merge: true,
          skip_merge: false
        }
      };
    });
  };

  private mapBulkResponseToItems(bulkResponse: IBulkCreateResponse): IItem[] {
    return bulkResponse.result.map((responseItem: IBulkCreateResponseItem) => {
      return {
        ...responseItem.object,
        merged: responseItem.merged
      }
    });
  }
}